import { useMemo } from "react";

import { ClmmPositionExpanded } from "@bridgesplit/abf-sdk";
import { TextVariant, TextColor, TooltipText } from "@bridgesplit/ui";
import { CheckCircleOutlined, ErrorOutlineOutlined } from "@mui/icons-material";
import { PositionUtil, PositionStatus } from "@orca-so/whirlpool-sdk";
import { isWhirlpoolPosition } from "@bridgesplit/abf-react";

import { RaydiumPositionInfo, RaydiumPositionSummarized } from "./raydium";
import { WhirlpoolPositionInfo, WhirlpoolPositionSummarized } from "./whirlpool";

export function ClmmPositionInfo({ position }: { position: ClmmPositionExpanded | undefined }) {
    if (!position) return null;

    if (isWhirlpoolPosition(position)) {
        return <WhirlpoolPositionInfo position={position} />;
    }

    return <RaydiumPositionInfo position={position} />;
}

export function ClmmPositionSummarized({
    position,
    spaceBetween
}: {
    position: ClmmPositionExpanded | undefined;
    spaceBetween?: boolean;
}) {
    if (!position) return null;

    if (isWhirlpoolPosition(position)) {
        return <WhirlpoolPositionSummarized position={position} spaceBetween={spaceBetween} />;
    }

    return <RaydiumPositionSummarized position={position} spaceBetween={spaceBetween} />;
}

export function PriceRangeStatus({
    tickCurrentIndex,
    tickLower,
    tickUpper,
    variant,
    iconOnly
}: {
    tickCurrentIndex: number | undefined;
    tickLower: number | undefined;
    tickUpper: number | undefined;
    variant?: TextVariant;
    iconOnly?: boolean;
}) {
    const details = useMemo(() => {
        return getPriceRangeStatus(tickCurrentIndex, tickLower, tickUpper);
    }, [tickCurrentIndex, tickLower, tickUpper]);

    if (!details) return null;

    const { color, icon, caption } = details;

    return (
        <TooltipText
            icon={false}
            helpText={iconOnly ? caption : undefined}
            sx={{ gap: 0.5 }}
            variant={variant}
            color={color}
        >
            {icon}
            {iconOnly ? null : caption}
        </TooltipText>
    );
}

type PriceRangeStatus = {
    color: TextColor;
    icon: React.ReactNode;
    caption: string;
} | null;

const CLOSE_TICK_THRESHOLD = 50; // e.g. within 50 ticks of a boundary, 0.5%

function getPriceRangeStatus(
    tickCurrentIndex: number | undefined,
    tickLower: number | undefined,
    tickUpper: number | undefined
): PriceRangeStatus {
    if (tickCurrentIndex === undefined || tickLower === undefined || tickUpper === undefined) return null;

    // distance in ticks
    const distanceToLower = Math.abs(tickCurrentIndex - tickLower);
    const distanceToUpper = Math.abs(tickUpper - tickCurrentIndex);

    const isCloseToBoundary = distanceToLower <= CLOSE_TICK_THRESHOLD || distanceToUpper <= CLOSE_TICK_THRESHOLD;

    const status = PositionUtil.getPositionStatus(tickCurrentIndex, tickLower, tickUpper);

    if (status === PositionStatus.InRange) {
        if (isCloseToBoundary) {
            return { color: "warning", icon: <ErrorOutlineOutlined />, caption: "Near range" };
        }
        return { color: "success", icon: <CheckCircleOutlined />, caption: "In range" };
    }

    return { color: "error", icon: <ErrorOutlineOutlined />, caption: "Out of range" };
}
